import React from "react";
import { useNavigate } from "react-router";

import { Delete as DeleteIcon, Refresh as RefreshIcon } from "@mui/icons-material";

import { OrderStatus, PurchaseOrderSummary, ResponseError, UserType } from "../apiClients/common";
import { deletePurchaseOrder, getPurchaseOrders, PurchaseOrderDetailsCollection } from "../apiClients/purchaseOrders";
import { AuthContext } from "../components/AuthContextProvider";
import { PurchaseOrderList } from "../components/PurchaseOrderList";
import { OrderPath } from "../constants";
import { DateFormat, formatDate } from "../helpers/dateHelper";
import { useBulkMutationCall } from "../hooks/useBulkMutationCall";
import { useQueryCall } from "../hooks/useQueryCall";
import { Action } from "../lib/ActionBar";
import { ConfirmDialog } from "../lib/ConfirmDialog";
import { FilterType } from "../lib/Filter";
import { ListingFilterProps, SelectMode } from "../lib/Listing";
import { NotificationManagerContext } from "../lib/NotificationManager";
import { NotificationType } from "../lib/NotificationTypes";
import { Page } from "../lib/Page";
import Messages from "../locale/en.json";

enum ActionIds {
  Refresh = "Refresh",
  Delete = "Delete",
}

enum PanelIds {
  Delete = "Delete",
}

enum FilterIds {
  From = "From",
  To = "To",
  Search = "Search",
}

export const CompletedOrders = (): JSX.Element => {
  const auth = React.useContext(AuthContext);
  const notificationManager = React.useContext(NotificationManagerContext);

  const navigate = useNavigate();

  const [search, setSearch] = React.useState<string>("");
  const [from, setFrom] = React.useState<Date>(
    new Date(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).setHours(0, 0, 0, 0))
  );
  const [to, setTo] = React.useState<Date>(new Date(new Date(Date.now()).setHours(23, 59, 59, 999)));

  const onToChange = (newTo: Date): void => {
    setTo(new Date(newTo.setHours(23, 59, 59, 999)));
  };

  const onFromChange = (newFrom: Date): void => {
    setFrom(new Date(newFrom.setHours(0, 0, 0, 0)));
  };

  const [purchaseOrders, setPurchaseOrders] = React.useState<PurchaseOrderDetailsCollection>();

  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = React.useState<PurchaseOrderDetailsCollection>();

  const [openPanel, setOpenPanel] = React.useState<PanelIds>();
  const closePanels = (): void => {
    setOpenPanel(undefined);
  };

  const { loading, refresh } = useQueryCall({
    method: getPurchaseOrders,
    args: { status: OrderStatus.Complete, from, to, search },
    onFailure: (err) => {
      if (err) {
        if (err.status === 401) {
          auth.logout();
        } else {
          notificationManager.push({
            type: NotificationType.Error,
            title: Messages.errors.loadCompleteOrders,
            message: err.message,
          });
        }
      }
    },
    onSuccess: setPurchaseOrders,
  });

  const { loading: deleteLoading, invoke: invokeDelete } = useBulkMutationCall({
    method: deletePurchaseOrder,
    onSuccess: refresh,
    onFailure: (error?: ResponseError[]) => {
      if (error?.length) {
        if (error.find((e) => e?.status === 401)) {
          auth.logout();
        } else {
          notificationManager.push({
            type: NotificationType.Error,
            title: Messages.errors.deleteOrder,
            message: error
              .map((err) => err?.message)
              .filter((message) => !!message)
              .join(" "),
          });
        }
      }
    },
  });

  const actions: Action[] = [
    {
      id: ActionIds.Refresh,
      disabled: loading,
      value: <RefreshIcon />,
      onClick: refresh,
    },
  ];

  if (auth.user?.type === UserType.SuperAdmin || auth.user?.type === UserType.Admin) {
    actions.push({
      id: ActionIds.Delete,
      value: <DeleteIcon />,
      disabled: selectedPurchaseOrders?.length !== 1 || deleteLoading,
      onClick: () => setOpenPanel(PanelIds.Delete),
    });
  }

  const navigateToPO = ({ id }: PurchaseOrderSummary): void => {
    navigate(`/${OrderPath}/${id}/`);
  };

  const getPanel = (): JSX.Element | null => {
    switch (openPanel) {
      case PanelIds.Delete: {
        let messagePrefix = "";

        if (selectedPurchaseOrders?.length) {
          if (selectedPurchaseOrders.length > 1) {
            messagePrefix = Messages.confirm.deletePurchaseOrders;
          } else {
            messagePrefix = Messages.confirm.deletePurchaseOrder;
          }
        }

        const message = messagePrefix + selectedPurchaseOrders?.map((po) => po.purchaseOrderNumber ?? po.id).join(", ");

        return (
          <ConfirmDialog
            message={message}
            onCancel={closePanels}
            onConfirm={() => {
              const poIds = selectedPurchaseOrders?.map(({ id }) => ({ id })) ?? [];
              invokeDelete(poIds);
              closePanels();
            }}
          />
        );
      }
      default:
        return null;
    }
  };

  const filters = [
    {
      id: FilterIds.Search,
      type: FilterType.Search,
      defaultValue: search,
      onChange: setSearch,
    },
    {
      id: FilterIds.From,
      type: FilterType.Date,
      label: Messages.common.from,
      defaultValue: from,
      onChange: onFromChange,
      // filter: (value: Date) => (item: PurchaseOrderSummary) => new Date(item.updatedOn) >= value,
      messageFormatter: (item?: Date) => (item ? formatDate(item, DateFormat.ShortDate) : ""),
    },
    {
      id: FilterIds.To,
      type: FilterType.Date,
      label: Messages.common.to,
      defaultValue: to,
      // server side
      onChange: onToChange,
      // client side
      // filter: (value: Date) => (item: PurchaseOrderSummary) => new Date(item.updatedOn) <= value,
      messageFormatter: (item?: Date) => (item ? formatDate(item, DateFormat.ShortDate) : ""),
    },
  ];

  const getOnSelect = (type?: UserType): ((items: PurchaseOrderSummary[]) => void) | undefined => {
    switch (type) {
      case UserType.SuperAdmin:
      case UserType.Admin:
        return setSelectedPurchaseOrders;
      case UserType.Client:
      default:
        return undefined;
    }
  };

  const getOnRowClick = (type?: UserType): ((item: PurchaseOrderSummary) => void) | undefined => {
    switch (type) {
      case UserType.SuperAdmin:
      case UserType.Admin:
      case UserType.Client:
        return navigateToPO;
      default:
        return undefined;
    }
  };

  return (
    <Page actions={actions} title={Messages.page.completedOrders.title}>
      <PurchaseOrderList
        loading={loading}
        selectionMode={
          auth.user?.type === UserType.SuperAdmin || auth.user?.type === UserType.Admin
            ? SelectMode.Single
            : SelectMode.None
        }
        filters={filters as ListingFilterProps<Date | string, PurchaseOrderSummary>[]}
        items={purchaseOrders ?? []}
        userType={auth.user?.type}
        onSelect={getOnSelect(auth.user?.type)}
        onRowClick={getOnRowClick(auth.user?.type)}
      />
      {getPanel()}
    </Page>
  );
};
